import { useAtom } from 'jotai';
import { toast } from 'react-hot-toast';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useChangePasswordMutation } from '../lib/queries';
import { ChangePasswordForm, ChangePasswordSchema } from '../lib/schemas';
import { Input } from './Input';
import { authAtom } from '../atoms';
import { TOAST_DURATION } from '../lib/constants';

export function ChangePassword() {
  const { t } = useTranslation();
  const [auth] = useAtom(authAtom);
  const { register, handleSubmit, formState } = useForm<ChangePasswordForm>({
    mode: 'all',
    resolver: zodResolver(ChangePasswordSchema),
  });
  const changePasswordMutation = useChangePasswordMutation();

  const onSubmit = (data: ChangePasswordForm) => {
    if (!auth?.user) return;
    changePasswordMutation.mutate(
      { passwordOld: data.passwordOld, password: data.passwordNew, id: auth.user.userId },
      {
        onSuccess: () => {
          toast.success(t('settings.passwordChanged'), { duration: TOAST_DURATION });
        },
        onError: (error: any) => {
          toast.error(
            t('settings.passwordChangeError', { message: t(`apiError.${error.message}`) }),
            {
              duration: TOAST_DURATION,
            },
          );
        },
      },
    );
  };

  return (
    <form method="post" className="col-span-2 font-semibold space-y-4">
      <Input
        type="password"
        {...register('passwordOld', { required: true })}
        label={t('settings.passwordOld')}
        placeholder="password"
        error={formState.errors.passwordOld?.message?.toString()}
        required
      />

      <Input
        type="password"
        {...register('passwordNew', { required: true })}
        label={t('settings.passwordNew')}
        placeholder="password"
        error={formState.errors.passwordNew?.message?.toString()}
        required
      />

      <Input
        type="password"
        {...register('passwordConfirm', { required: true })}
        label={t('settings.passwordConfirm')}
        placeholder="password"
        error={formState.errors.passwordConfirm?.message?.toString()}
        required
      />
      <div className="flex justify-end items-center gap-2 mt-4">
        <button
          className="primary-fill"
          type="submit"
          disabled={!formState.isValid}
          onClick={handleSubmit(onSubmit)}
        >
          {t('settings.change')}
        </button>
      </div>
    </form>
  );
}
